/* A tiny chainable async interface with class-based steps.
 * Features:
 *  - ClassName.then(NextClass) for sequential chaining
 *  - Multiple .when(condition, TargetClass, label?) for branching (evaluated in order)
 *  - ClassName.invoke(initialState) to execute the chain (internal runner)
 *  - ClassName.graph({ direction?: 'TD'|'LR' }) -> Mermaid flowchart
 *  - Optional retries via .retries(n), and error routing via .catch(ErrorClass)
 * 
 * NEW: Function-based approach
 *  - chainstep(fn) creates chainable steps from functions
 *  - Fully typed with automatic type inference
 */

export type State = Record<string, any>;

export type StepClass<S extends State = State> =
  (new () => ChainStep<S>) &
  typeof ChainStep<S>;

type Branch<S extends State> = {
  cond: (state: S) => boolean;
  target: StepClass<S>;
  label?: string;
};

// Function-based step type
export type StepFunction<S extends State> = (state: S) => Promise<S> | S;

export abstract class ChainStep<S extends State> {
  // ----- Static chain definition storage (per subclass) -----
  // These are per-class; assigning via static methods writes onto the subclass constructor.
  protected static _next?: StepClass<any>;
  protected static _branches?: Branch<any>[];
  protected static _maxRetries?: number;
  protected static _onError?: StepClass<any>;

  // ----- Implement in subclasses -----
  abstract execute(state: S): Promise<S>;

  // ----- Chain definition DSL -----
  static then<S extends State>(this: StepClass<S>, next: StepClass<S>): StepClass<S> {
    (this as any)._next = next;
    return next;
  }

  static when<S extends State>(
    this: StepClass<S>,
    cond: (state: S) => boolean,
    target: StepClass<S>,
    label?: string
  ): StepClass<S> {
    const ctor = this as any;
    if (!ctor._branches) ctor._branches = [];
    ctor._branches.push({ cond, target, label });
    // Return the same class to allow chaining multiple .when calls
    return this;
  }

  /** Set maximum retries for this step (default 0). */
  static retries<S extends State>(this: StepClass<S>, n: number): StepClass<S> {
    (this as any)._maxRetries = Math.max(0, n | 0);
    return this;
  }

  /** Route to a handler step if this step ultimately fails after retries. */
  static catch<S extends State>(this: StepClass<S>, errorStep: StepClass<S>): StepClass<S> {
    (this as any)._onError = errorStep;
    return this;
  }

  // ----- Execution (internal runner via recursion) -----
  static async invoke<S extends State>(this: StepClass<S>, initial: S): Promise<{
    result: any;
    state: S;
    path: string[];
    metadata: {
      steps: any[];
      totalDuration: number;
      startTime: number;
      endTime: number;
    };
  }> {
    const ctor = this as any as StepClass<S>;
    const instance = new ctor();
    const name = ctor.name || 'Step';
    const start = Date.now();

    // Ensure meta log exists
    (initial as any)._meta ??= [];
    const metaEntry: any = { step: name, attempts: 0, start };

    const maxRetries = ((ctor as any)._maxRetries ?? 0) as number;

    let state = initial;
    let attempt = 0;
    while (true) {
      attempt++;
      metaEntry.attempts = attempt;
      try {
        state = await instance.execute(state);
        metaEntry.ok = true;
        break;
      } catch (err: any) {
        metaEntry.ok = false;
        metaEntry.error = err?.message ?? String(err);
        if (attempt <= maxRetries) {
          // optional simple backoff; adjust as desired
          await new Promise(r => setTimeout(r, Math.min(250 * attempt, 1000)));
          continue; // retry
        }
        // route to error handler if present
        const onError = (ctor as any)._onError as StepClass<S> | undefined;
        metaEntry.end = Date.now();
        metaEntry.durationMs = metaEntry.end - metaEntry.start;
        (state as any)._meta.push(metaEntry);
        if (onError) {
          return onError.invoke(state);
        }
        throw err;
      }
    }

    metaEntry.end = Date.now();
    metaEntry.durationMs = metaEntry.end - metaEntry.start;
    (state as any)._meta.push(metaEntry);

    // Determine next step: evaluate branches in order, else default .then
    const branches = (ctor as any)._branches as Branch<S>[] | undefined;
    if (branches && branches.length) {
      for (const b of branches) {
        let chosen = false;
        try {
          chosen = !!b.cond(state);
        } catch {
          // ignore condition errors; treat as false
        }
        if (chosen) {
          return (b.target as StepClass<S>).invoke(state);
        }
      }
    }

    const next = (ctor as any)._next as StepClass<S> | undefined;
    if (next) {
      return next.invoke(state);
    }

    // Extract clean result (excluding internal properties)
    const { _meta, ...cleanResult } = state as any;
    const result = (state as any).result !== undefined ? (state as any).result : cleanResult;
    
    // Extract execution path for separate access (this is different from state.path)
    const executionPath = (state as any).executionPath || [];
    
    // Create metadata object
    const endTime = Date.now();
    const metadata = {
      steps: (state as any)._meta || [],
      totalDuration: endTime - start,
      startTime: start,
      endTime: endTime
    };

    // Return structured result
    return {
      result,
      state,
      path: executionPath,
      metadata
    };
  }

  // ----- Mermaid graph generation -----
  static graph<S extends State>(
    this: StepClass<S>,
    opts?: { direction?: 'TD' | 'LR' }
  ): string {
    const dir = opts?.direction ?? 'TD';
    const lines: string[] = [`graph ${dir}`];

    const seen = new Set<StepClass<S>>();
    const visit = (cls: StepClass<S>) => {
      if (seen.has(cls)) return;
      seen.add(cls);

      const name = (cls as any).name || 'Step';
      const branches = ((cls as any)._branches ?? []) as Branch<S>[];
      const next = (cls as any)._next as StepClass<S> | undefined;

      // Emit branch edges first (with optional labels)
      for (const b of branches) {
        const targetName = ((b.target as any).name) || 'Step';
        const label = b.label ? `|${escapeLabel(b.label)}|` : '';
        lines.push(`  ${name} -->${label} ${targetName}`);
        visit(b.target);
      }

      // Emit default next edge
      if (next) {
        const targetName = ((next as any).name) || 'Step';
        lines.push(`  ${name} --> ${targetName}`);
        visit(next);
      }

      // If no outgoing edges, you can mark it as terminal (optional)
      if (!branches.length && !next) {
        // No-op; Mermaid infers terminal nodes
      }
    };

    visit(this as StepClass<S>);
    return lines.join('\n');
  }
}

function escapeLabel(s: string): string {
  return s.replace(/\|/g, '\\|');
}

// Function-based step class
class FunctionStep<S extends State> extends ChainStep<S> {
  constructor(
    private fn: StepFunction<S>,
    private stepName: string = 'FunctionStep'
  ) {
    super();
  }

  async execute(state: S): Promise<S> {
    const result = this.fn(state);
    return result instanceof Promise ? result : Promise.resolve(result);
  }

  // Override the name for better debugging
  static get name() {
    return this.prototype.stepName || 'FunctionStep';
  }
}

// Function to create chainable steps from functions
export function chainstep<S extends State>(
  fn: StepFunction<S>,
  name?: string
): StepClass<S> {
  const StepClass = class extends FunctionStep<S> {
    constructor() {
      super(fn, name);
    }
  };
  
  // Set the name for better debugging
  Object.defineProperty(StepClass, 'name', {
    value: name || 'ChainStep',
    writable: false,
    configurable: true
  });
  
  return StepClass as StepClass<S>;
}
